import { useMemo } from "react";
import { generateHTML } from "@tiptap/core";
import { useTextBlockInteraction } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/RichText/useTextBlockInteraction";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { TextRendererProps } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/RichText/TextRenderer.types";
import {
  createTipTapFromText,
  isRichTextContent,
  sanitizeTipTapContent,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/richTextHelpers";
import type { TipTapContent } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textTypes";
import {
  VisualEmailTiptapEditor,
  getVisualEmailTiptapExtensions,
} from "./VisualEmailTiptapEditor";

export const convertTipTapToHTML = (content: TipTapContent): string => {
  if (!content?.content) return "";
  const sanitizedContent = sanitizeTipTapContent(content);
  return generateHTML(sanitizedContent, getVisualEmailTiptapExtensions());
};

export const VisualEmailTextRenderer = ({
  blockPath,
  text = "",
  className,
  placeholder,
}: TextRendererProps) => {
  const isInlineEditing = useBuilderStore(state => state.isInlineEditing);
  const isCurrentlyInlineEditing = isInlineEditing(blockPath);
  const { handleDoubleClick, handleBlur } = useTextBlockInteraction({
    blockPath,
    text,
  });

  const htmlContent = useMemo(() => {
    if (isRichTextContent(text)) {
      return convertTipTapToHTML(text);
    }
    return null;
  }, [text]);

  if (isCurrentlyInlineEditing) {
    const editorValue = isRichTextContent(text)
      ? text
      : createTipTapFromText(text);
    return (
      <VisualEmailTiptapEditor
        value={editorValue}
        onBlur={handleBlur}
        placeholder={placeholder}
        shouldFocusWhenReady
      />
    );
  }

  if (htmlContent) {
    return (
      <div
        className={className}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  return (
    <span className={className} onDoubleClick={handleDoubleClick}>
      {text || placeholder}
    </span>
  );
};
